import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  AnyDoc,
  AssetMeta,
  Background,
  CanvasSettings,
  CarouselDoc,
  CarouselLayer,
  Filters,
  ID,
  Project,
  Slot,
  SlotTransform,
  SplitterDoc,
  Template,
  ToolId,
} from '../types';
import {
  createCarousel,
  createProject,
  createSplitter,
  makeImageLayer,
  makeSlot,
  makeTextLayer,
  syncSlots,
  BASE_EDGE,
} from './defaults';
import {
  emptyHistory,
  pushSnapshot,
  resetHistory,
  shouldCoalesce,
  takeRedo,
  takeUndo,
  type HistorySlice,
} from './history';
import { canMerge, cellsInOrder, createGrid, mergeCells, splitCell } from '../template';
import { ASPECT_PRESETS, SLIDE_PRESETS } from '../presets';
import { slideFrame } from '../slice';
import { clamp, shuffle, uid } from '../utils';

export type InspectorTab =
  | 'layout'
  | 'image'
  | 'style'
  | 'grid'
  | 'source'
  | 'slides'
  | 'layers';
export type Theme = 'light' | 'dark' | 'system';

/** tool → the Store key holding its document. */
export const DOC_KEY = {
  collage: 'project',
  splitter: 'splitter',
  carousel: 'carousel',
} as const;
export type DocKey = (typeof DOC_KEY)[ToolId];

export interface Toast {
  id: string;
  message: string;
  tone: 'info' | 'error' | 'success';
}

interface UiState {
  selection: ID[];
  tab: InspectorTab;
  theme: Theme;
  zoomMode: 'fit' | 'manual';
  zoom: number;
  exportOpen: boolean;
  shortcutsOpen: boolean;
  /** false, or the mode the tool chooser is showing in */
  chooserOpen: false | 'firstRun' | 'modal';
  leftOpen: boolean;
  rightOpen: boolean;
  busy: string | null;
  toasts: Toast[];
  hoveredCell: ID | null;
  /** which carousel slide the viewport is centred on — where new layers land */
  slide: number;
}

interface Store {
  tool: ToolId;
  /** the collage document — name and position deliberately unchanged */
  project: Project;
  splitter: SplitterDoc;
  carousel: CarouselDoc;
  history: Record<ToolId, HistorySlice>;
  ui: UiState;

  activeDoc: () => AnyDoc;
  setTool: (tool: ToolId) => void;

  update: (recipe: (p: Project) => void, mergeKey?: string) => void;
  updateSplitter: (recipe: (d: SplitterDoc) => void, mergeKey?: string) => void;
  updateCarousel: (recipe: (d: CarouselDoc) => void, mergeKey?: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  setUi: (patch: Partial<UiState>) => void;
  toast: (message: string, tone?: Toast['tone']) => void;
  dismissToast: (id: string) => void;

  select: (id: ID | null, additive?: boolean) => void;
  selectAll: () => void;

  addAssets: (metas: AssetMeta[]) => void;
  deleteAsset: (id: ID) => void;
  clearLibrary: () => void;

  assign: (cellId: ID, assetId: ID | null) => void;
  swapCells: (a: ID, b: ID) => void;
  clearCell: (cellId: ID) => void;
  autoFill: (opts?: { shuffle?: boolean; overwrite?: boolean }) => void;
  clearAllCells: () => void;

  applyTemplate: (template: Template) => void;
  setGrid: (cols: number, rows: number) => void;
  mergeSelection: () => void;
  splitSelected: (dir: 'vertical' | 'horizontal') => void;

  patchSlot: (cellId: ID, patch: Partial<Slot>, mergeKey?: string) => void;
  patchTransform: (cellId: ID, patch: Partial<SlotTransform>, mergeKey?: string) => void;
  patchFilters: (cellIds: ID[], patch: Partial<Filters>, mergeKey?: string) => void;
  setFilters: (cellIds: ID[], filters: Filters) => void;
  resetSlotTransform: (cellId: ID) => void;

  /* carousel */
  addImageLayer: (assetId: ID) => void;
  addTextLayer: () => void;
  updateLayers: (ids: ID[], patch: (layer: CarouselLayer) => void, mergeKey?: string) => void;
  fillStrip: (ids: ID[]) => void;
  removeLayers: (ids: ID[]) => void;
  duplicateLayers: (ids: ID[]) => void;
  reorderLayer: (id: ID, to: 'up' | 'down' | 'front' | 'back') => void;
  setSlides: (count: number) => void;
  setSlideAspect: (presetId: string) => void;
  setCarouselBackground: (bg: Background, mergeKey?: string) => void;

  /* splitter */
  setSource: (assetId: ID | null) => void;
  patchSplitter: (patch: Partial<SplitterDoc>, mergeKey?: string) => void;
  setSplitGrid: (cols: number, rows: number) => void;
  patchSourceTransform: (patch: Partial<SlotTransform>, mergeKey?: string) => void;
  patchSourceFilters: (patch: Partial<Filters>, mergeKey?: string) => void;
  setSourceFilters: (filters: Filters) => void;
  resetSource: () => void;

  patchCanvas: (patch: Partial<CanvasSettings>, mergeKey?: string) => void;
  setBackground: (bg: Background, mergeKey?: string) => void;
  setAspect: (aspectId: string) => void;

  hydrate: (docs: Partial<Record<ToolId, AnyDoc>>) => void;
  loadDoc: (tool: ToolId, doc: AnyDoc) => void;
  newDoc: (tool: ToolId) => void;
  rename: (name: string) => void;
}

/**
 * The four things the store cannot know on its own.
 *
 * Everything else here — the documents, the history, every action — is the
 * same arithmetic on both platforms, which is why the store lives in the
 * engine rather than being written twice. What differs is where the theme was
 * saved, what "the tool this entry point is for" means, whether the screen is
 * wide enough for two docked panels, and how a photo's pixels are released.
 */
export interface StoreHost {
  /** The theme the user last chose. */
  loadTheme: () => Theme;
  /**
   * Which tool to open. The web has a page per tool and answers with that
   * page's tool; a phone has one entry point and answers with the last one
   * used, or null to accept the default.
   */
  bootTool: () => ToolId | null;
  /** Is there room for both side panels at once? False on a phone. */
  panelsStartOpen: () => boolean;
  /** Release a photo's decoded pixels and its stored blob. */
  dropAsset: (id: ID) => void;
}

/** Ids the current tool's selection may legally contain. */
function selectableIds(doc: AnyDoc): ID[] {
  if ('template' in doc) return doc.template.cells.map((c) => c.id);
  if ('layers' in doc) return doc.layers.map((l) => l.id);
  return [];
}

/** Drop every reference to an asset from whichever document shape this is. */
function detachAsset(doc: AnyDoc, id: ID | null) {
  const gone = (candidate: ID | null) => (id === null ? true : candidate === id);

  if ('slots' in doc) {
    for (const slot of Object.values(doc.slots)) if (gone(slot.assetId)) slot.assetId = null;
    if (doc.canvas.background.type === 'image' && gone(doc.canvas.background.assetId)) {
      doc.canvas.background = { type: 'solid', color: '#ffffff' };
    }
    return;
  }
  if ('source' in doc) {
    if (gone(doc.source.assetId)) doc.source.assetId = null;
  } else {
    for (const layer of doc.layers) {
      if (layer.type === 'image' && gone(layer.assetId)) layer.assetId = null;
    }
  }
  if (doc.background.type === 'image' && gone(doc.background.assetId)) {
    doc.background = { type: 'solid', color: '#ffffff' };
  }
}

export function createStore(host: StoreHost) {
  return create<Store>()(
  immer((set, get) => {
    /**
     * The single mutation gate, shared by all three tools: snapshot the old
     * document (unless this edit coalesces into the previous one), apply the
     * recipe, then restore the tool's own invariants.
     */
    function commit<K extends DocKey>(
      tool: ToolId,
      key: K,
      recipe: (draft: Store[K]) => void,
      mergeKey?: string,
    ) {
      const prev = get()[key];
      const now = Date.now();
      const coalesce = shouldCoalesce(get().history[tool], mergeKey, now);

      set((s) => {
        const h = s.history[tool];
        if (!coalesce) pushSnapshot(h, prev, now);
        h.mergeKey = mergeKey ?? null;
        h.mergeAt = now;
        recipe(s[key] as Store[K]);
        if (key === 'project') syncSlots(s.project);
      });
    }

    /** Edit whichever document is active — used by the shared asset actions. */
    function commitActive(recipe: (doc: AnyDoc) => void, mergeKey?: string) {
      const tool = get().tool;
      commit(tool, DOC_KEY[tool], recipe as (d: AnyDoc) => void, mergeKey);
    }

    return {
      tool: host.bootTool() ?? 'collage',
      project: createProject(),
      splitter: createSplitter(),
      carousel: createCarousel(),
      history: {
        collage: emptyHistory(),
        splitter: emptyHistory(),
        carousel: emptyHistory(),
      },
      ui: {
        selection: [],
        tab: 'layout',
        theme: host.loadTheme(),
        zoomMode: 'fit',
        zoom: 1,
        exportOpen: false,
        shortcutsOpen: false,
        chooserOpen: false,
        leftOpen: host.panelsStartOpen(),
        rightOpen: host.panelsStartOpen(),
        busy: null,
        toasts: [],
        hoveredCell: null,
        slide: 0,
      },

      activeDoc: () => get()[DOC_KEY[get().tool]],

      setTool(tool) {
        if (get().tool === tool) return;
        set((s) => {
          s.tool = tool;
          s.ui.selection = [];
          s.ui.zoomMode = 'fit';
          s.ui.zoom = 1;
        });
      },

      update: (recipe, mergeKey) => commit('collage', 'project', recipe, mergeKey),
      updateSplitter: (recipe, mergeKey) => commit('splitter', 'splitter', recipe, mergeKey),
      updateCarousel: (recipe, mergeKey) => commit('carousel', 'carousel', recipe, mergeKey),

      undo() {
        const { tool } = get();
        const key = DOC_KEY[tool];
        const current = get()[key];
        set((s) => {
          const prev = takeUndo(s.history[tool], current) as Store[typeof key] | undefined;
          if (prev === undefined) return;
          (s as Record<string, unknown>)[key] = prev;
          const ids = new Set(selectableIds(prev));
          s.ui.selection = s.ui.selection.filter((id) => ids.has(id));
        });
      },

      redo() {
        const { tool } = get();
        const key = DOC_KEY[tool];
        const current = get()[key];
        set((s) => {
          const next = takeRedo(s.history[tool], current) as Store[typeof key] | undefined;
          if (next === undefined) return;
          (s as Record<string, unknown>)[key] = next;
          const ids = new Set(selectableIds(next));
          s.ui.selection = s.ui.selection.filter((id) => ids.has(id));
        });
      },

      canUndo: () => get().history[get().tool].past.length > 0,
      canRedo: () => get().history[get().tool].future.length > 0,

      setUi(patch) {
        set((s) => {
          Object.assign(s.ui, patch);
        });
      },

      toast(message, tone = 'info') {
        const id = uid('toast');
        set((s) => {
          s.ui.toasts.push({ id, message, tone });
          if (s.ui.toasts.length > 3) s.ui.toasts.shift();
        });
        setTimeout(() => get().dismissToast(id), 3600);
      },

      dismissToast(id) {
        set((s) => {
          s.ui.toasts = s.ui.toasts.filter((t) => t.id !== id);
        });
      },

      select(id, additive = false) {
        set((s) => {
          if (id === null) {
            s.ui.selection = [];
            return;
          }
          if (!additive) {
            s.ui.selection = [id];
            return;
          }
          s.ui.selection = s.ui.selection.includes(id)
            ? s.ui.selection.filter((x) => x !== id)
            : [...s.ui.selection, id];
        });
      },

      selectAll() {
        const ids = selectableIds(get().activeDoc());
        set((s) => {
          s.ui.selection = ids;
        });
      },

      addAssets(metas) {
        if (!metas.length) return;
        commitActive((doc) => {
          for (const meta of metas) {
            doc.assets[meta.id] = meta;
            if (!doc.assetOrder.includes(meta.id)) doc.assetOrder.push(meta.id);
          }
        });
      },

      deleteAsset(id) {
        commitActive((doc) => {
          delete doc.assets[id];
          doc.assetOrder = doc.assetOrder.filter((a) => a !== id);
          detachAsset(doc, id);
        });
        // Only surrender the decoded image and its blob once no other tool's
        // document still references it — the blob store is shared.
        const s = get();
        const stillUsed = (['collage', 'splitter', 'carousel'] as ToolId[]).some((t) =>
          s[DOC_KEY[t]].assetOrder.includes(id),
        );
        if (!stillUsed) host.dropAsset(id);
      },

      clearLibrary() {
        const before = get().activeDoc().assetOrder.slice();
        commitActive((doc) => {
          doc.assets = {};
          doc.assetOrder = [];
          detachAsset(doc, null);
        });
        const s = get();
        for (const id of before) {
          const stillUsed = (['collage', 'splitter', 'carousel'] as ToolId[]).some((t) =>
            s[DOC_KEY[t]].assetOrder.includes(id),
          );
          if (!stillUsed) host.dropAsset(id);
        }
      },

      assign(cellId, assetId) {
        get().update((p) => {
          const slot = p.slots[cellId];
          if (!slot) return;
          const fresh = makeSlot();
          slot.assetId = assetId;
          slot.transform = fresh.transform;
        });
      },

      swapCells(a, b) {
        if (a === b) return;
        get().update((p) => {
          const sa = p.slots[a];
          const sb = p.slots[b];
          if (!sa || !sb) return;
          p.slots[a] = sb;
          p.slots[b] = sa;
        });
      },

      clearCell(cellId) {
        get().update((p) => {
          p.slots[cellId] = makeSlot();
        });
      },

      autoFill(opts = {}) {
        const { project } = get();
        const order = cellsInOrder(project.template).map((c) => c.id);
        const used = new Set(
          Object.values(project.slots)
            .map((s) => s.assetId)
            .filter(Boolean) as ID[],
        );
        let pool = project.assetOrder.filter((id) => opts.overwrite || !used.has(id));
        if (opts.shuffle) pool = shuffle(pool);
        if (!pool.length) {
          get().toast('No photos left to place', 'error');
          return;
        }
        get().update((p) => {
          let i = 0;
          for (const cellId of order) {
            const slot = p.slots[cellId];
            if (!slot) continue;
            if (!opts.overwrite && slot.assetId) continue;
            if (i >= pool.length) break;
            slot.assetId = pool[i++];
            slot.transform = makeSlot().transform;
          }
        });
      },

      clearAllCells() {
        get().update((p) => {
          for (const id of Object.keys(p.slots)) p.slots[id] = makeSlot();
        });
      },

      applyTemplate(template) {
        const { project } = get();
        const previousAssets = cellsInOrder(project.template)
          .map((c) => project.slots[c.id])
          .filter((s): s is Slot => !!s);
        get().update((p) => {
          p.template = template;
          p.slots = {};
          const order = cellsInOrder(template);
          order.forEach((cell, i) => {
            const carried = previousAssets[i];
            p.slots[cell.id] = carried
              ? { ...carried, transform: makeSlot().transform }
              : makeSlot();
          });
        });
        set((s) => {
          s.ui.selection = [];
        });
      },

      setGrid(cols, rows) {
        const c = clamp(Math.round(cols), 1, 12);
        const r = clamp(Math.round(rows), 1, 12);
        get().applyTemplate(createGrid(c, r));
      },

      mergeSelection() {
        const { project, ui } = get();
        if (!canMerge(project.template, ui.selection)) {
          get().toast('Select adjacent cells forming a rectangle', 'error');
          return;
        }
        const keep = ui.selection[0];
        get().update((p) => {
          p.template = mergeCells(p.template, ui.selection);
        });
        set((s) => {
          s.ui.selection = [keep];
        });
      },

      splitSelected(dir) {
        const { ui } = get();
        if (ui.selection.length !== 1) {
          get().toast('Select a single cell to split', 'error');
          return;
        }
        const id = ui.selection[0];
        get().update((p) => {
          const { template } = splitCell(p.template, id, dir);
          p.template = template;
        });
      },

      patchSlot(cellId, patch, mergeKey) {
        get().update((p) => {
          const slot = p.slots[cellId];
          if (slot) Object.assign(slot, patch);
        }, mergeKey);
      },

      patchTransform(cellId, patch, mergeKey) {
        get().update((p) => {
          const slot = p.slots[cellId];
          if (slot) Object.assign(slot.transform, patch);
        }, mergeKey);
      },

      patchFilters(cellIds, patch, mergeKey) {
        get().update((p) => {
          for (const id of cellIds) {
            const slot = p.slots[id];
            if (slot) Object.assign(slot.filters, patch);
          }
        }, mergeKey);
      },

      setFilters(cellIds, filters) {
        get().update((p) => {
          for (const id of cellIds) {
            const slot = p.slots[id];
            if (slot) slot.filters = { ...filters };
          }
        });
      },

      resetSlotTransform(cellId) {
        get().update((p) => {
          const slot = p.slots[cellId];
          if (slot) slot.transform = makeSlot().transform;
        });
      },

      addImageLayer(assetId) {
        const { carousel, ui } = get();
        const asset = get().carousel.assets[assetId];
        const slide = clamp(ui.slide, 0, carousel.slides - 1);
        // Sized to most of the slide and centred on the one being looked at —
        // the usual next move is to drag it across a boundary.
        const w = carousel.slideW * 0.7;
        const ratio = asset ? asset.height / asset.width : 1;
        const h = Math.min(carousel.slideH * 0.7, w * ratio);
        const layer = makeImageLayer(assetId, {
          x: slide * carousel.slideW + (carousel.slideW - w) / 2,
          y: (carousel.slideH - h) / 2,
          w,
          h,
        });
        layer.name = asset?.name ?? 'Image';
        get().updateCarousel((d) => {
          d.layers.push(layer);
        });
        set((st) => {
          st.ui.selection = [layer.id];
          st.ui.tab = 'style';
        });
      },

      addTextLayer() {
        const { carousel, ui } = get();
        const slide = clamp(ui.slide, 0, carousel.slides - 1);
        const w = carousel.slideW * 0.8;
        const h = carousel.slideH * 0.16;
        const layer = makeTextLayer({
          x: slide * carousel.slideW + (carousel.slideW - w) / 2,
          y: carousel.slideH * 0.08,
          w,
          h,
        });
        get().updateCarousel((d) => {
          d.layers.push(layer);
        });
        set((st) => {
          st.ui.selection = [layer.id];
          st.ui.tab = 'style';
        });
      },

      updateLayers(ids, patch, mergeKey) {
        if (!ids.length) return;
        const wanted = new Set(ids);
        get().updateCarousel((d) => {
          for (const layer of d.layers) {
            if (wanted.has(layer.id) && !layer.locked) patch(layer);
          }
        }, mergeKey);
      },

      fillStrip(ids) {
        const { carousel } = get();
        const frame = slideFrame(carousel);
        get().updateLayers(ids, (l) => {
          l.x = 0;
          l.y = 0;
          l.w = frame.width;
          l.h = frame.height;
          // A rotated layer would leave the strip's corners bare, and the
          // whole point of this is that nothing is left bare.
          l.rotation = 0;
          if (l.type === 'image') {
            // Cover, framed dead centre: a full bleed with no letterboxing,
            // whatever the photo's aspect and however many slides it spans.
            l.fit = 'cover';
            l.transform = { ...l.transform, x: 0, y: 0, scale: 1 };
          } else {
            l.autoHeight = false;
          }
        });
      },

      removeLayers(ids) {
        const wanted = new Set(ids);
        get().updateCarousel((d) => {
          d.layers = d.layers.filter((l) => !wanted.has(l.id));
        });
        set((st) => {
          st.ui.selection = st.ui.selection.filter((id) => !wanted.has(id));
        });
      },

      duplicateLayers(ids) {
        const wanted = new Set(ids);
        const copies: ID[] = [];
        get().updateCarousel((d) => {
          for (const layer of d.layers.filter((l) => wanted.has(l.id))) {
            const copy = { ...layer, id: uid('layer'), x: layer.x + 24, y: layer.y + 24 };
            copies.push(copy.id);
            d.layers.push(copy);
          }
        });
        set((st) => {
          st.ui.selection = copies;
        });
      },

      reorderLayer(id, to) {
        get().updateCarousel((d) => {
          const from = d.layers.findIndex((l) => l.id === id);
          if (from < 0) return;
          const [layer] = d.layers.splice(from, 1);
          const target =
            to === 'front'
              ? d.layers.length
              : to === 'back'
                ? 0
                : clamp(from + (to === 'up' ? 1 : -1), 0, d.layers.length);
          d.layers.splice(target, 0, layer);
        });
      },


      setSlides(count) {
        get().updateCarousel((d) => {
          d.slides = clamp(Math.round(count), 2, 10);
        });
      },

      setSlideAspect(presetId) {
        const preset = SLIDE_PRESETS.find((p) => p.id === presetId);
        if (!preset) return;
        const { carousel } = get();
        // Keep the composition where it was relative to the strip rather than
        // stranding every layer off-canvas when the slide shape changes.
        const sx = preset.w / carousel.slideW;
        const sy = preset.h / carousel.slideH;
        get().updateCarousel((d) => {
          d.slideAspect = presetId;
          d.slideW = preset.w;
          d.slideH = preset.h;
          for (const layer of d.layers) {
            layer.x *= sx;
            layer.y *= sy;
            layer.w *= sx;
            layer.h *= sy;
            if (layer.type === 'text') {
              layer.size *= sy;
              layer.letterSpacing *= sy;
            }
          }
        });
      },

      setCarouselBackground(bg, mergeKey) {
        get().updateCarousel((d) => {
          d.background = bg;
        }, mergeKey);
      },

      setSource(assetId) {
        get().updateSplitter((d) => {
          d.source.assetId = assetId;
          d.source.transform = makeSlot().transform;
        });
      },

      patchSplitter(patch, mergeKey) {
        get().updateSplitter((d) => {
          Object.assign(d, patch);
        }, mergeKey);
      },

      setSplitGrid(cols, rows) {
        get().updateSplitter((d) => {
          d.cols = clamp(Math.round(cols), 1, 10);
          d.rows = clamp(Math.round(rows), 1, 10);
        });
      },

      patchSourceTransform(patch, mergeKey) {
        get().updateSplitter((d) => {
          Object.assign(d.source.transform, patch);
        }, mergeKey);
      },

      patchSourceFilters(patch, mergeKey) {
        get().updateSplitter((d) => {
          Object.assign(d.source.filters, patch);
        }, mergeKey);
      },

      setSourceFilters(filters) {
        get().updateSplitter((d) => {
          d.source.filters = { ...filters };
        });
      },

      resetSource() {
        get().updateSplitter((d) => {
          d.source.transform = makeSlot().transform;
        });
      },

      patchCanvas(patch, mergeKey) {
        get().update((p) => {
          Object.assign(p.canvas, patch);
        }, mergeKey);
      },

      setBackground(bg, mergeKey) {
        get().update((p) => {
          p.canvas.background = bg;
        }, mergeKey);
      },

      setAspect(aspectId) {
        const preset = ASPECT_PRESETS.find((a) => a.id === aspectId);
        if (!preset) return;
        const width = preset.ratio >= 1 ? BASE_EDGE : Math.round(BASE_EDGE * preset.ratio);
        const height = preset.ratio >= 1 ? Math.round(BASE_EDGE / preset.ratio) : BASE_EDGE;
        get().update((p) => {
          p.canvas.aspect = aspectId;
          p.canvas.width = width;
          p.canvas.height = height;
        });
      },

      /** One `set` for the whole restored session — one re-render, not three. */
      hydrate(docs) {
        set((s) => {
          for (const tool of Object.keys(docs) as ToolId[]) {
            const doc = docs[tool];
            if (!doc) continue;
            (s as Record<string, unknown>)[DOC_KEY[tool]] = doc;
            resetHistory(s.history[tool]);
          }
          s.ui.selection = [];
        });
      },

      loadDoc(tool, doc) {
        set((s) => {
          (s as Record<string, unknown>)[DOC_KEY[tool]] = doc;
          resetHistory(s.history[tool]);
          s.tool = tool;
          s.ui.selection = [];
        });
      },


      /**
       * Reset one tool's document. Deliberately does NOT touch the shared blob
       * store — the other two tools' photos live there too; the autosave pass
       * prunes whatever is genuinely unreferenced.
       */
      newDoc(tool) {
        const fresh: AnyDoc =
          tool === 'collage'
            ? createProject()
            : tool === 'splitter'
              ? createSplitter()
              : createCarousel();
        get().loadDoc(tool, fresh);
      },

      rename(name) {
        const tool = get().tool;
        commit(
          tool,
          DOC_KEY[tool],
          (doc) => {
            (doc as AnyDoc).name = name;
          },
          'rename',
        );
      },
    };
  }),
  );
}

export type CollageStore = ReturnType<typeof createStore>;

export const selectSelectedCells = (s: Store) => s.ui.selection;
